import React from 'react';
import { FaCheck } from 'react-icons/fa';
import classNames from 'classnames';
import { twMerge } from 'tailwind-merge';
import { useFeatureList, Features } from '../../../utils/FeatureUtils';
import useBusinessTranslation from '../../../hooks/useBusinessTranslation';

interface FeaturesFormProps {
  features: Features;
  onFeaturesChange: (features: Features) => void;
  className?: string;
}

interface FeatureCardProps {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  isSelected: boolean;
  onToggle: () => void;
}

function FeatureCard({ icon: Icon, title, description, isSelected, onToggle }: FeatureCardProps) {
  return (
    <div
      onClick={onToggle}
      className={classNames(
        'group relative cursor-pointer rounded-xl border-2 p-4 pt-2 transition-all duration-300 hover:shadow-md',
        {
          'border-primary-900 bg-opacity-10 shadow-sm': isSelected,
          'border-secondary-200 bg-secondary-200 bg-opacity-30 hover:border-primary-900 hover:bg-opacity-40':
            !isSelected,
        },
      )}>
      <div
        className={classNames(
          'absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full border-2 transition-all duration-200',
          {
            'border-primary-900 bg-primary-900': isSelected,
            'border-secondary-200 group-hover:border-primary-900': !isSelected,
          },
        )}>
        {isSelected && <FaCheck className="h-2.5 w-2.5 text-secondary-50" />}
      </div>

      <div
        className={classNames(
          'mb-3 flex h-10 w-10 items-center justify-center rounded-lg transition-all duration-200',
          {
            'bg-primary-900 text-secondary-200': isSelected,
            'bg-secondary-200 text-primary-900':
              !isSelected,
          },
        )}>
        <Icon className="h-5 w-5" />
      </div>

      <div className="space-y-1">
        <h3 className="text-base font-semibold text-primary-900">{title}</h3>
        <p className="text-xs leading-relaxed text-primary-900 opacity-75">{description}</p>
      </div>
    </div>
  );
}

export default function FeaturesForm({
  features,
  onFeaturesChange,
  className,
}: FeaturesFormProps): JSX.Element {
  const featureList = useFeatureList();
  const { t } = useBusinessTranslation();

  return (
    <div className={twMerge('flex flex-1 flex-col', className)}>
      <div
        className="grid gap-3 lg:gap-4"
        style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))' }}>
        {featureList.map((config) => (
          <FeatureCard
            key={config.key}
            icon={config.icon}
            title={config.title}
            description={config.description}
            isSelected={Boolean(features[config.key])}
            onToggle={() => {
              onFeaturesChange({ ...features, [config.key]: !features[config.key] });
            }}
          />
        ))}
      </div>
    </div>
  );
}
